import {LeagueConfig} from '@chancer/common/lib/interfaces/firestore/LeaguesEnums';

export interface LeagueCapabilities {
  fixtures: boolean;
  liveScores: boolean;
  tabOdds: boolean;
  postGameKbWrite: boolean;
  cronSync: boolean;
  knowledgeBase: boolean;
  syncable: boolean;
}

// These used to be three sets of league ids maintained here, apart from the
// leagues themselves — a shape that had already gone wrong elsewhere, where a
// second copy of a league's competition slug drifted from the first and left
// internationals unable to grade a player question. They live on the league now.
//
// The two below stay: they are keyed by provider, not league, and describe what
// a provider can do rather than what a league is.
const SYNCABLE_PROVIDERS = new Set(['nrl', 'rugbypass', 'tabnz']);

const LIVE_SCORE_PROVIDERS = new Set([
  'nrl',
  'rugbypass',
  'world_rugby',
  'tabnz',
  'sofascore',
  'radar',
  'espn',
  'pulselive',
  'sportradar_eui',
]);

export const getLeagueCapabilities = (config: LeagueConfig): LeagueCapabilities => {
  const hasTabOdds =
    config.provider === 'tabnz' ||
    ('tabCategory' in config && !!config.tabCategory);

  return {
    fixtures: true,
    liveScores: LIVE_SCORE_PROVIDERS.has(config.provider),
    tabOdds: hasTabOdds,
    postGameKbWrite: config.postGameKbWrite ?? false,
    cronSync: config.cronSync ?? false,
    knowledgeBase: config.knowledgeBase ?? false,
    syncable: SYNCABLE_PROVIDERS.has(config.provider),
  };
};

export const getDataSourceUrl = (config: LeagueConfig): string | undefined => {
  switch (config.provider) {
    case 'rugbypass':
      return `https://www.rugbypass.com/${config.competitionSlug}/fixtures-results/`;
    case 'nrl':
      return `https://www.nrl.com/draw/?competition=${config.competition}`;
    case 'tabnz':
      return `https://www.tab.co.nz/sports/${config.sport}/${config.competitionSlug}/`;
    case 'world_rugby':
      return `https://www.world.rugby/${config.competitionSlug}/`;
    case 'sofascore':
      return `https://www.sofascore.com/tournament/${config.sport}/${config.eventsPathGroup}/${config.eventsPathLeague}`;
    case 'sportradar_eui':
      return `https://embed-api.eui.connect.sportradar.com/v1/embed/${config.embedId}/fixtures?entityId=${config.entityId}`;
    default:
      return undefined;
  }
};

export const getTabSourceUrl = (config: LeagueConfig): string | undefined => {
  if (config.provider === 'tabnz') {
    return `https://www.tab.co.nz/sports/${config.sport}/${config.competitionSlug}/`;
  }
  if ('tabCategory' in config && config.tabCategory && 'tabCompetitionSlug' in config && config.tabCompetitionSlug) {
    return `https://www.tab.co.nz/sports/${config.tabCategory.toLowerCase().replace('_', '-')}/${config.tabCompetitionSlug}/`;
  }
  return undefined;
};

export interface ConfigField {
  key: string;
  value: string;
  isUrl?: boolean;
}

export const getConfigFields = (config: LeagueConfig): ConfigField[] => {
  const fields: ConfigField[] = [
    {key: 'League ID', value: config.id},
    {key: 'Provider', value: config.provider},
    {key: 'Sport', value: config.sport},
    {key: 'Country', value: config.country},
  ];

  switch (config.provider) {
    case 'rugbypass':
      fields.push({key: 'Competition Slug', value: config.competitionSlug});
      if (config.tabCategory) { fields.push({key: 'TAB Category', value: config.tabCategory}); }
      if (config.tabCompetitionSlug) { fields.push({key: 'TAB Slug', value: config.tabCompetitionSlug}); }
      if (config.tabSha256Hash) { fields.push({key: 'TAB Query Hash', value: config.tabSha256Hash.slice(0, 16) + '…'}); }
      break;
    case 'nrl':
      fields.push({key: 'Competition ID', value: config.competition});
      if (config.tabCategory) { fields.push({key: 'TAB Category', value: config.tabCategory}); }
      if (config.tabCompetitionSlug) { fields.push({key: 'TAB Slug', value: config.tabCompetitionSlug}); }
      break;
    case 'tabnz':
      fields.push({key: 'TAB Category', value: config.category});
      fields.push({key: 'TAB Competition Slug', value: config.competitionSlug});
      if (config.regionSlug) { fields.push({key: 'TAB Region', value: config.regionSlug}); }
      fields.push({key: 'Query Hash', value: config.sha256Hash.slice(0, 16) + '…'});
      break;
    case 'world_rugby':
      fields.push({key: 'Competition Slug', value: config.competitionSlug});
      if (config.tabCompetitionSlug) { fields.push({key: 'TAB Slug', value: config.tabCompetitionSlug}); }
      break;
    case 'sofascore':
      fields.push({key: 'Tournament ID', value: config.uniqueTournamentId});
      fields.push({key: 'Season ID', value: config.seasonId});
      break;
    case 'genius':
      fields.push({key: 'Competition ID', value: config.competitionId});
      break;
    case 'radar':
      fields.push({key: 'Season ID', value: config.seasonId});
      fields.push({key: 'API Version', value: config.apiVersion});
      break;
    case 'sportradar_eui':
      fields.push({key: 'Embed ID', value: config.embedId});
      fields.push({key: 'Entity ID', value: config.entityId});
      fields.push({key: 'Competition ID', value: config.competitionId});
      break;
  }

  return fields;
};

export const CAPABILITY_LABELS: Record<keyof LeagueCapabilities, {label: string; description: string}> = {
  fixtures: {label: 'Fixtures', description: 'Schedule and results fetched automatically'},
  liveScores: {label: 'Live Scores', description: 'Score polling during matches'},
  tabOdds: {label: 'TAB Odds', description: 'Pre-match and in-play odds from TAB NZ'},
  postGameKbWrite: {label: 'KB Write', description: 'Knowledge base updated on game completion'},
  cronSync: {label: 'Cron Sync', description: 'Weekly scheduled sync of standings and results'},
  knowledgeBase: {label: 'KB Files', description: 'Knowledge base files exist in GCS'},
  syncable: {label: 'Sync', description: 'Manual fixture sync available'},
};
